import React, { ReactNode } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Stack,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface ActionsCardProps {
  onResimulate?: () => void;
  isResimulating?: boolean;
  results?: any;
  onResetForm?: () => void;
  titleComponent?: ReactNode;
  children?: ReactNode;
  variant?: 'full' | 'compact';
  elevation?: number;
}

const ActionsCard: React.FC<ActionsCardProps> = ({ 
  onResimulate,
  isResimulating,
  results,
  onResetForm,
  titleComponent,
  children,
  variant = 'full',
  elevation = 3
}) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    if (results?.id) {
      navigate(`/input?id=${results.id}`);
    }
  };

  if (variant === 'compact') {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: { xs: 'center', sm: 'flex-end' },
        width: '100%'
      }}>
        <Card 
          elevation={elevation}
          sx={{
            backgroundColor: (theme) => theme.palette.background.paper,
            border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            borderRadius: 2,
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 'fit-content' }
          }}
        >
          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 1 }}
              alignItems="center"
              justifyContent="center"
            >
              <Box sx={{ 
                display: { xs: 'flex', sm: 'none' }, 
                alignItems: 'center', 
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
                justifyContent: 'center',
                mb: { xs: 0.5, sm: 0 }
              }}>
                <Chip 
                  label="Quick Actions" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              
              {children}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Card 
      elevation={elevation}
      sx={{
        backgroundColor: (theme) => theme.palette.background.paper,
        color: 'text.primary',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
      }}
    >
      <CardContent sx={{ position: 'relative', p: 3 }}>
        {titleComponent || (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
              Actions
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              What would you like to do next?
            </Typography>
          </Box>
        )}
        
        {children ? (
          <Box>
            {children}
          </Box>
        ) : (
          <Stack 
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 2 }}
            sx={{ mt: 2 }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={onResimulate}
              disabled={isResimulating}
              sx={{ 
                flex: 1,
                py: 1.5,
                '&:disabled': {
                  backgroundColor: 'rgba(0,0,0,0.12)',
                }
              }}
            >
              {isResimulating ? 'Updating...' : 'Re-simulate'}
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              onClick={handleEdit}
              disabled={isResimulating}
              sx={{ 
                flex: 1,
                py: 1.5,
              }}
            >
              Edit
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              onClick={onResetForm}
              disabled={isResimulating}
              sx={{ 
                flex: 1,
                py: 1.5,
              }}
            >
              Start Over
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default ActionsCard; 
